'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useRollbar } from '@rollbar/react';
import RefreshIcon from '@mui/icons-material/Refresh';
import UpdateIcon from '@mui/icons-material/Update';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Dialog } from 'src/components/Molecules/Dialog';
import { isChunkLoadError } from './isChunkLoadError';
import { FlexColumn } from 'src/components/Atoms/FlexBox';
interface ChunkLoadErrorFallbackProps {
  error: unknown;
  resetErrorBoundary: () => void;
}
const ChunkLoadErrorFallback: React.FC<ChunkLoadErrorFallbackProps> = t0 => {
  const $ = _c(22);
  const {
    error
  } = t0;
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [countdown, setCountdown] = React.useState(10);
  const rollbar = useRollbar();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setIsRefreshing(true);
      setTimeout(_temp, 500);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleRefresh = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setCountdown(0);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleTryAgain = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      handleRefresh();
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleClose = t3;
  let t4;
  let t5;
  if ($[3] !== countdown) {
    t4 = () => {
      if (countdown > 0) {
        const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        return () => clearTimeout(timer);
      } else {
        handleRefresh();
      }
    };
    t5 = [countdown, handleRefresh];
    $[3] = countdown;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] !== error || $[7] !== rollbar) {
    t6 = () => {
      rollbar.error("Chunk Load Error - Auto-refresh triggered", error as Error, {
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: Date.now()
      });
    };
    t7 = [error, rollbar];
    $[6] = error;
    $[7] = rollbar;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useEffect(t6, t7);
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      textAlign: "center",
      py: 2
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t10;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <FlexColumn alignItems="center" justifyContent="center" gap={1} mb={2}><UpdateIcon color="warning" /></FlexColumn>;
    t10 = <Typography variant="body1" gutterBottom={true}>A new version of the app has been deployed. To ensure you have the latest features and avoid any issues, the page will refresh automatically.</Typography>;
    $[11] = t10;
    $[12] = t9;
  } else {
    t10 = $[11];
    t9 = $[12];
  }
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      mt: 3
    };
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== countdown || $[15] !== isRefreshing) {
    t12 = <FlexColumn sx={t11}>{isRefreshing ? <FlexColumn sx={{
        alignItems: "center",
        gap: 2
      }}><CircularProgress size={40} /><Typography variant="body2" color="text.secondary">Refreshing...</Typography></FlexColumn> : <FlexColumn><Typography variant="body2" color="text.secondary">Auto-refresh in{" "}{countdown}{" "}seconds</Typography><FlexColumn sx={{
          mt: 2,
          justifyContent: "center",
          gap: 1
        }}><CircularProgress variant="determinate" value={(10 - countdown) * 10} size={24} thickness={4} /></FlexColumn></FlexColumn>}</FlexColumn>;
    $[14] = countdown;
    $[15] = isRefreshing;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== isRefreshing) {
    t13 = !isRefreshing && <FlexColumn sx={{
      mt: 3,
      justifyContent: "center"
    }}><Button variant="contained" onClick={handleTryAgain} startIcon={<RefreshIcon />} disabled={isRefreshing}>Refresh Now</Button></FlexColumn>;
    $[17] = isRefreshing;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t12 || $[20] !== t13) {
    t14 = <Dialog open={true} title="App Update Available" handleClose={handleClose}><FlexColumn sx={t8}>{t9}{t10}{t12}{t13}</FlexColumn></Dialog>;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  return t14;
};
interface ChunkLoadErrorHandlerProps {
  children: React.ReactNode;
}
const ChunkLoadErrorHandler: React.FC<ChunkLoadErrorHandlerProps> = t0 => {
  const $ = _c(8);
  const {
    children
  } = t0;
  const rollbar = useRollbar();
  let t1;
  let t2;
  if ($[0] !== rollbar) {
    t1 = () => {
      const handleUnhandledRejection = event => {
        const error = event.reason;
        if (isChunkLoadError(error)) {
          console.warn("Chunk load error detected:", error);
          rollbar.warning("Chunk Load Error - Unhandled Rejection", error, {
            userAgent: navigator.userAgent,
            url: window.location.href,
            timestamp: Date.now()
          });
          event.preventDefault();
          setTimeout(_temp2, 1000);
        }
      };
      window.addEventListener("unhandledrejection", handleUnhandledRejection);
      return () => {
        window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      };
    };
    t2 = [rollbar];
    $[0] = rollbar;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== rollbar) {
    t3 = (error_0, errorInfo) => {
      const typedError = error_0 as Error;
      if (isChunkLoadError(typedError)) {
        rollbar.error("Chunk Load Error - Error Boundary", typedError, {
          errorInfo,
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: Date.now()
        });
        return;
      }
      rollbar.error("Non-chunk error in ChunkLoadErrorHandler", typedError, errorInfo);
      throw typedError;
    };
    $[3] = rollbar;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleError = t3;
  let t4;
  if ($[5] !== children || $[6] !== handleError) {
    t4 = <ErrorBoundary FallbackComponent={ChunkLoadErrorFallback} onError={handleError} onReset={_temp3}>{children}</ErrorBoundary>;
    $[5] = children;
    $[6] = handleError;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
export default ChunkLoadErrorHandler;
export { ChunkLoadErrorFallback };
function _temp() {
  window.location.reload();
}
function _temp2() {
  window.location.reload();
}
function _temp3() {
  if ("webpackChunkName" in window) {
    delete (window as unknown as {
      webpackChunkName?: string;
    }).webpackChunkName;
  }
}